import React from 'react'
import styled, { css } from 'styled-components'
import { mote_primary } from '@mote/config/defaults/colors'
import AnywherePNG from './assets/anywhere.png'
import BrowserOutlinePNG from './assets/browser-outline.png'
import ClassroomPNG from './assets/classroom.png'
import DocsPNG from './assets/docs.png'
import FormsPNG from './assets/forms.png'
import GmailPNG from './assets/gmail.png'
import SheetsPNG from './assets/sheets.png'
import SlidesPNG from './assets/slides.png'

import MoteLogoPNG from './assets/mote-logo.png'
import ClassroomLogoPNG from './assets/classroom-logo.png'
import DocsLogoPNG from './assets/docs-logo.png'
import FormsLogoPNG from './assets/forms-logo.png'
import GmailLogoPNG from './assets/gmail-logo.png'
import SheetsLogoPNG from './assets/sheets-logo.png'
import SlidesLogoPNG from './assets/slides-logo.png'

import DEVELOPER_GET_IN_TOUCH_SVG from './assets/developer-get-in-touch.svg'
import SUGGEST_AN_INTEGRATION_SVG from './assets/suggest-an-integration.svg'

import { Block, Button, Carousel, Text } from '@mote/ui'

const allImages = [
  AnywherePNG,
  ClassroomPNG,
  SlidesPNG,
  DocsPNG,
  FormsPNG,
  SheetsPNG,
  GmailPNG
]
const allIcons = [
  MoteLogoPNG,
  ClassroomLogoPNG,
  SlidesLogoPNG,
  DocsLogoPNG,
  FormsLogoPNG,
  SheetsLogoPNG,
  GmailLogoPNG
]

const content = [
  {
    title: 'On any webpage!',
    description:
      'Don’t just share a weblink - share a sticky mote! Use Mote to leave a voice note on any website and bring your link to life.'
  },
  {
    title: 'Google Classroom',
    description:
      'Mote integrates with Private Comments, Class Comments and Announcements.'
  },
  {
    title: 'Google Slides',
    description:
      'Record and insert audio directly into a Google Slides at the click of a button.'
  },
  {
    title: 'Google Docs',
    description:
      'Share fast, friendly voice note feedback and comments in Google Docs.'
  },
  {
    title: 'Google Forms',
    description:
      'Create audio questions, answer-choices & feedback in Google Forms. Recipients can also respond with audio!'
  },
  {
    title: 'Google Sheets',
    description:
      'Play Mote voice notes from within Google Sheets - a perfect match for our Google Forms integration.'
  },
  {
    title: 'Gmail',
    description:
      'Tired of typing? Spice up your emails with Mote voice messaging, integrated directly into Gmail.'
  }
]

export const DemoSection = () => {
  const slider = React.useRef(null)
  const [activeSlide, setActiveSlide] = React.useState(0)
  const [stopAutoplay, setStopAutoplay] = React.useState(false)

  const settings = {
    arrows: false,
    dots: false,
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: !stopAutoplay,
    autoplaySpeed: 6000,
    beforeChange: (current, next) => setActiveSlide(next)
  }

  function carouselImages() {
    return allImages.map((image, index) => (
      <img
        key={`carousel-image--${index}`}
        src={image}
        alt={`Carousel item ${index}`}
      />
    ))
  }

  function carouselIcons() {
    return allIcons.map((image, index) => (
      <div key={`carousel-icon--${index}`}>
        <Icon
          active={activeSlide === index}
          onClick={() => {
            slider.current.slickGoTo(index)
            setStopAutoplay(true)
          }}
          src={image}
        />
      </div>
    ))
  }

  function contentCopy() {
    const { title, description } = content[activeSlide]
    return (
      <Details>
        <Header>Mote works where you do</Header>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </Details>
    )
  }

  return (
    <Root gap={75}>
      <DemoContainer>
        <CarouselBase>
          <SliderContainer>
            <Carousel ref={slider} settings={settings}>
              {carouselImages()}
            </Carousel>
          </SliderContainer>
        </CarouselBase>

        <Content>
          {contentCopy()}

          <IconsContainer>{carouselIcons()}</IconsContainer>
        </Content>
      </DemoContainer>

      <GetInTouchContainer
        layout='horizontal'
        align='center'
        justify='center'
        wrapped
      >
        <GetInTouchSection layout='horizontal' align='center' gap={55}>
          <img src={SUGGEST_AN_INTEGRATION_SVG} alt='Suggest an integration' />

          <Block gap={28} align='flex-start'>
            <Text fontSize={24} fontWeight={500}>
              Where else would you like to use Mote?{' '}
            </Text>
            <Button
              tag='a'
              href='https://mote.canny.io/integrations'
              text='Suggest an integration'
              size='sm'
            />
          </Block>
        </GetInTouchSection>

        <GetInTouchSection layout='horizontal' align='center' gap={55}>
          <img src={DEVELOPER_GET_IN_TOUCH_SVG} alt='Get in touch' />

          <Block gap={28} align='flex-start'>
            <Text fontSize={24} fontWeight={500}>
              Are you a developer looking to integrate voice messaging?
            </Text>
            <Button tag='a' href='/developers' text='Get in touch' size='sm' />
          </Block>
        </GetInTouchSection>
      </GetInTouchContainer>
    </Root>
  )
}

const Root = styled(Block)`
  padding-bottom: 48px;
`

// TODO: Move out breakpoint values to settings
const DemoContainer = styled.div`
  background: linear-gradient(to bottom, ${mote_primary} 57%, white 57%);
  display: flex;
  justify-content: center;
  padding-top: 96px;

  @media (max-width: 1199px) {
    background: linear-gradient(to bottom, ${mote_primary} 62%, white 62%);
  }

  @media (max-width: 991px) {
    flex-direction: column-reverse;
    display: flex;
    align-items: center;
    background: linear-gradient(to bottom, ${mote_primary} 30%, white 30%);
    padding-top: 60px;
  }

  @media (max-width: 639px) {
    background: linear-gradient(to bottom, ${mote_primary} 42%, white 42%);
  }
`

const GetInTouchContainer = styled(Block)`
  & > :first-of-type {
    padding: 39px 35px 39px 0;
    margin-right: 40px;
  }

  & > :last-of-type {
    padding: 39px 35px 39px 50px;
  }

  @media (max-width: 1144px) {
    & > :first-of-type {
      padding: 29px 25px 29px 0;
      margin-right: 30px;
      width: 450px;
    }

    & > :last-of-type {
      padding: 29px 25px 29px 20px;
      width: 450px;
    }
  }

  @media (max-width: 992px) {
    flex-direction: column;
    & > :first-of-type {
      margin-right: 0px;
      margin-bottom: 20px;
    }
  }
`

const GetInTouchSection = styled(Block)`
  width: 520px;
  background: #f9f9fb;
  border-radius: 10px;

  img {
    width: 160px;
  }
`

const Content = styled.div`
  z-index: 1;
  color: white;
`

const Details = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 639px) {
    padding: 0 40px;
  }
`

const Header = styled.div`
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 16px;
  line-height: 120%;

  @media (max-width: 1199px) {
    font-size: 16px;
  }
`

const Title = styled.div`
  font-weight: 700;
  font-size: 32px;
  margin-bottom: 16px;
  line-height: 120%;

  @media (max-width: 1199px) {
    font-size: 28px;
  }

  @media (max-width: 991px) {
    font-size: 24px;
  }

  @media (max-width: 639px) {
    font-size: 21px;
  }
`

const Description = styled.div`
  font-weight: 400;
  font-size: 18px;
  line-height: 150%;
  width: 520px;
  margin-bottom: 48px;

  @media (max-width: 639px) {
    margin-bottom: 32px;
    width: 100%;
  }
`

const CarouselBase = styled.div`
  background-image: url(${BrowserOutlinePNG});
  background-position: center top;
  background-size: cover;
  background-repeat: no-repeat;
  width: 520px;
  height: 378px;
  position: relative;
  filter: drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.05));
  margin-right: 40px;

  @media (max-width: 1199px) {
    width: 431px;
    height: 313px;
    margin-right: 30px;
  }

  @media (max-width: 991px) {
    width: 574px;
    height: 417px;
    margin-right: 0;
  }

  @media (max-width: 639px) {
    width: 286px;
    height: 207px;
    margin-right: 0;
  }
`

const SliderContainer = styled.div`
  width: 520px;
  height: 340px;
  margin-top: 38px;

  @media (max-width: 1199px) {
    width: 431px;
    height: 313px;
  }

  @media (max-width: 991px) {
    width: 574px;
    height: 417px;
  }

  @media (max-width: 639px) {
    width: 286px;
    height: 207px;
    margin-top: 20px;
  }
`

const IconsContainer = styled.div`
  display: flex;
  height: 52px;
  margin-top: 32px;

  @media (max-width: 1199px) {
    height: 42px;
  }

  @media (max-width: 991px) {
    margin-bottom: 32px;
  }

  @media (max-width: 639px) {
    margin: 32px auto;
    width: 300px;
    height: 52px;
    overflow-x: scroll;
  }
`

const Icon = styled.img`
  margin-right: 16px;
  opacity: 0.2;

  ${({ active }) =>
    active &&
    css`
      opacity: 1;
    `}

  &:hover {
    opacity: 1;
    cursor: pointer;
  }
`
